import { useCallback, useRef, useState, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Block from '@uiw/react-color-block';
import { useClickOutside } from '@react-hookz/web/esm/useClickOutside';
import { useKeyboardEvent } from '@react-hookz/web/esm/useKeyboardEvent';

export const ColorSelector = (props) => {
    const { label, colors, disabled, defaultColor, selectedColor, onSelect } = props;

    const [showPicker, setShowPicker] = useState(false);
    const [color, setColor] = useState(selectedColor);

    useEffect(() => { setColor(selectedColor); }, [selectedColor]);

    const [position, setPosition] = useState({ top: 0, left: 0 });

    const buttonRef = useRef(null);
    const pickerRef = useRef(null);

    const onChange = useCallback((color) => {
        setColor(color.hex);
        onSelect(color.hex);
        setShowPicker(false);
    }, [onSelect]);

    const onClear = useCallback(() => {
        setColor(defaultColor);
        onSelect(defaultColor);
    }, [onSelect, defaultColor]);

    useClickOutside(pickerRef, () => {
        setShowPicker(false);
    });

    useKeyboardEvent(
        true,
        (ev) => {
            if (ev.key === 'Escape') {
                setShowPicker(false);
            }
        },
        [],
        { eventOptions: { passive: true } }
    );

    useLayoutEffect(() => {
        if (!showPicker || !buttonRef.current || !pickerRef.current) {
            return;
        }

        const buttonRect = buttonRef.current.getBoundingClientRect();
        const pickerRect = pickerRef.current.getBoundingClientRect();

        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const margin = 8;

        // Horizontal centering
        let left =
            buttonRect.left +
            buttonRect.width / 2 -
            pickerRect.width / 2;

        // Clamp horizontally
        left = Math.max(
            margin,
            Math.min(left, viewportWidth - pickerRect.width - margin)
        );

        // Decide whether to open below or above
        const spaceBelow = viewportHeight - buttonRect.bottom;

        const top =
            spaceBelow >= pickerRect.height + margin
                ? buttonRect.bottom + margin
                : buttonRect.top - pickerRect.height - margin;

        setPosition({ top, left });
    }, [showPicker]);

    return (
        <div className="flex flex-row grow items-center">
            <div>{label}</div>

            <div className="mx-1.5">
                <div className={`border ${!disabled
                    ? "border-black hover:border-red-600"
                    : "border-gray-400 cursor-not-allowed"
                    }`}>
                    <div
                        ref={buttonRef}
                        className={`m-1 ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
                        style={{
                            width: '15px',
                            height: '15px',
                            backgroundColor: color,
                            opacity: disabled ? 0.5 : 1.0
                        }}
                        disabled={disabled}
                        onClick={() => {
                            if (disabled) {
                                return;
                            }

                            setShowPicker(true)
                        }
                        }
                    />
                </div>
            </div>

            <div className="ml-1 mr-5">
                <button
                    disabled={disabled}
                    onClick={onClear}
                    type="button"
                    className={`inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white focus:outline-none ${disabled ? "cursor-not-allowed bg-gray-400" : "bg-green-600 cursor-pointer hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"}`}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill="currentColor" className="bi bi-trash" viewBox="0 0 16 16"> <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" /> <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" /> </svg>
                </button>
            </div>

            {showPicker && createPortal(
                <div
                    ref={pickerRef}
                    style={{
                        position: 'fixed',
                        top: position.top,
                        left: position.left,
                        zIndex: 10000,
                        outline: '2px solid #000',
                        outlineOffset: '0px',
                        borderRadius: '8px'
                    }}
                >
                    <Block
                        color={color}
                        colors={colors}
                        onChange={onChange}
                    />
                </div>,
                document.body
            )}
        </div>
    );
};